/* MEZUIX PDP — variant change, color takeover, sticky ATC, qty controls */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.__mezInit) return;
    root.__mezInit = true;
    // Stash the product's default featured image URL for variants without their own
    root.__mezDefaultImg =
      (root.querySelector('[data-mez-main-image]') ||
        root.querySelector('.mez-pdp__main-img') ||
        {}).src || null;

    const sectionId = root.id.replace(/^mez-pdp-/, '');
    const dataKey =
      'MEZUIX_PRODUCT_' + sectionId.replace(/-/g, '_');
    const productData = window[dataKey];
    if (!productData) return;

    const variantsByOptions = new Map();
    productData.variants.forEach((v) => {
      const key = [v.option1, v.option2, v.option3].filter(Boolean).join('||');
      variantsByOptions.set(key, v);
    });

    const form = root.querySelector('#mez-pdp-form');
    const variantInput = root.querySelector('[data-mez-variant-id]');
    const swatches = Array.from(root.querySelectorAll('[data-mez-swatch]'));
    const optSelectedLabels = Array.from(
      root.querySelectorAll('[data-mez-opt-selected]')
    );
    const mainImg =
      root.querySelector('[data-mez-main-image]') ||
      root.querySelector('.mez-pdp__main-img');
    const stickyImg = root.querySelector('[data-mez-sticky-image]');
    const stickyPrice = root.querySelector('[data-mez-sticky-price]');
    const stickyLabel = root.querySelector('[data-mez-sticky-label]');
    const stickyAtc = root.querySelector('[data-mez-sticky-atc]');
    const stickyEl = root.querySelector('[data-mez-sticky]');
    const atcWrap = root.querySelector('[data-mez-atc-wrap]');
    const atc = root.querySelector('[data-mez-atc]');
    const atcLabel = root.querySelector('[data-mez-atc-label]');
    const notifyLink = root.querySelector('[data-mez-notify]');
    const priceEl = root.querySelector('[data-mez-price]');
    const subPriceEl = root.querySelector('[data-mez-sub-price]');
    const thumbs = Array.from(root.querySelectorAll('[data-mez-pdp-thumb]'));
    const modes = Array.from(root.querySelectorAll('[data-mez-mode]'));

    const state = {
      selected: { 1: null, 2: null, 3: null },
      currentVariant: null,
      mode: 'onetime',
    };

    // Detect default-active mode from HTML (subscribe may be default)
    const __defaultActiveMode = root.querySelector('[data-mez-mode].mez-pdp__mode--active');
    if (__defaultActiveMode) state.mode = __defaultActiveMode.dataset.mezMode;

    // Seed selected options from currently-active swatches
    swatches.forEach((sw) => {
      if (sw.classList.contains('mez-pdp__swatch--active')) {
        const pos = parseInt(sw.dataset.optionPosition, 10);
        state.selected[pos] = sw.dataset.optionValue;
      }
    });

    // ---------- Variant resolution ----------
    function resolveVariant() {
      const key = [state.selected[1], state.selected[2], state.selected[3]]
        .filter(Boolean)
        .join('||');
      return variantsByOptions.get(key) || null;
    }

    function applyVariant(variant) {
      if (!variant) return;
      state.currentVariant = variant;

      // Variant id on form
      if (variantInput) variantInput.value = variant.id;

      // Featured image swap. If the variant has no image, fall back to the
      // product's default featured image rather than going blank.
      const targetSrc = variant.featured_image || root.__mezDefaultImg;
      if (mainImg && targetSrc && mainImg.src !== targetSrc) {
        // Helper defined below; clears srcset/sizes before changing src so
        // the browser doesn't keep painting an older srcset candidate.
        // We can call it directly here — it's hoisted.
        swapMainImage(targetSrc, variant.title);
      }
      if (stickyImg && targetSrc) {
        stickyImg.removeAttribute('srcset');
        stickyImg.removeAttribute('sizes');
        stickyImg.src = targetSrc;
      }

      // Coming-soon detection (metafield or oos+treat-flag)
      const isComing =
        variant.coming_soon === true ||
        (productData.treatOosAsComing && variant.available === false);

      // Price update
      if (priceEl) {
        const compare = variant.compare_at_price_formatted;
        priceEl.innerHTML =
          (compare
            ? '<span class="mez-pdp__price-compare">' +
              compare +
              '</span>'
            : '') + variant.price_formatted;
      }
      if (stickyPrice) stickyPrice.textContent = variant.price_formatted;

      // Subscribe price preview
      if (subPriceEl) {
        const discountPct = (productData.subscribeDiscount || 0) / 100;
        const cents = Math.round(parseFloat(variant.price) * (1 - discountPct));
        subPriceEl.textContent = formatMoney(cents);
      }

      // ATC state
      const cantBuy = isComing || variant.available === false;
      const atcText = cantBuy
        ? root.dataset.comingSoonLabel ||
          (atcLabel ? atcLabel.dataset.comingSoonLabel : null) ||
          'Coming Soon'
        : root.dataset.atcLabel ||
          (atcLabel ? atcLabel.dataset.atcLabel : null) ||
          'Add to bag';

      if (atc) {
        atc.disabled = cantBuy;
        atc.dataset.comingSoon = isComing ? '1' : '0';
      }
      if (atcLabel) atcLabel.textContent = atcText;
      if (stickyAtc) {
        stickyAtc.disabled = cantBuy;
      }
      if (stickyLabel) stickyLabel.textContent = atcText;
      if (notifyLink) {
        notifyLink.hidden = !isComing;
      }

      // Update selected-option label badges
      optSelectedLabels.forEach((el) => {
        const pos = parseInt(el.dataset.mezOptSelected, 10);
        el.textContent = state.selected[pos] || '';
      });

      // Color takeover — read from active swatch
      const activeSwatch = swatches.find(
        (sw) =>
          sw.dataset.variantId &&
          sw.dataset.variantId === String(variant.id)
      );
      if (activeSwatch && activeSwatch.dataset.flavorColor) {
        root.style.setProperty(
          '--mez-pdp-accent',
          activeSwatch.dataset.flavorColor
        );
      }
    }

    function formatMoney(cents) {
      // Best-effort fallback; Shopify Money filters do this server-side.
      // For accuracy, a sub app would replace this entirely.
      const dollars = (cents / 100).toFixed(2);
      return '$' + dollars;
    }

    // ---------- Swatch selection (click + keyboard via radio change) ----------
    function selectSwatch(sw) {
      const pos = parseInt(sw.dataset.optionPosition, 10);
      const val = sw.dataset.optionValue;
      const input = sw.querySelector('input[type="radio"]');
      if (input) input.checked = true;
      swatches.forEach((other) => {
        if (parseInt(other.dataset.optionPosition, 10) === pos) {
          other.classList.toggle('mez-pdp__swatch--active', other === sw);
        }
      });
      state.selected[pos] = val;
      const v = resolveVariant();
      if (v) applyVariant(v);
    }
    swatches.forEach((sw) => {
      sw.addEventListener('click', () => selectSwatch(sw));
      // Keyboard: arrow keys move focus + native radio change fires here
      const input = sw.querySelector('input[type="radio"]');
      if (input) {
        input.addEventListener('change', () => {
          if (input.checked) selectSwatch(sw);
        });
      }
    });

    // ---------- Subscribe / one-time mode + frequency ----------
    const sellingPlanInput = root.querySelector('[data-mez-selling-plan-input]');
    const frequencySelect = root.querySelector('[data-mez-frequency-select]');

    function activeFrequencyPlan() {
      if (frequencySelect && frequencySelect.value) return frequencySelect.value;
      const subLabel = root.querySelector('[data-mez-mode="subscribe"]');
      return (subLabel && subLabel.dataset.mezSellingPlan) || '';
    }

    function applyMode(mode) {
      state.mode = mode;
      modes.forEach((x) =>
        x.classList.toggle('mez-pdp__mode--active', x.dataset.mezMode === mode)
      );
      const active = modes.find((x) => x.dataset.mezMode === mode);
      if (active) {
        const radio = active.querySelector('input[type="radio"]');
        if (radio) radio.checked = true;
      }

      if (mode === 'subscribe') {
        if (sellingPlanInput) sellingPlanInput.value = activeFrequencyPlan();
        if (priceEl && subPriceEl) {
          priceEl.innerHTML = subPriceEl.textContent;
        }
      } else {
        if (sellingPlanInput) sellingPlanInput.value = '';
        if (state.currentVariant) applyVariant(state.currentVariant);
      }
    }

    modes.forEach((m) => {
      m.addEventListener('click', (e) => {
        // Prevent clicks inside the dropdown / select from re-triggering mode
        if (e.target.closest('[data-mez-frequency-select]')) return;
        applyMode(m.dataset.mezMode);
      });
    });

    if (frequencySelect) {
      frequencySelect.addEventListener('change', () => {
        if (state.mode === 'subscribe' && sellingPlanInput) {
          sellingPlanInput.value = frequencySelect.value;
        }
      });
      frequencySelect.addEventListener('click', (e) => e.stopPropagation());
    }

    // ---------- Quantity stepper ----------
    const qtyInput = root.querySelector('[data-mez-qty-input]');
    root.querySelectorAll('[data-mez-qty]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!qtyInput) return;
        const delta = parseInt(btn.dataset.mezQty, 10) || 0;
        const next = Math.max(1, parseInt(qtyInput.value, 10) + delta);
        qtyInput.value = next;
      });
    });

    // ---------- Thumbs ----------
    function swapMainImage(newSrc, newAlt) {
      if (!mainImg || !newSrc) return;
      // Preload the new image first so the swap is instant — old image stays
      // visible during load (no blank box), then snaps to the new one.
      const preload = new Image();
      preload.onload = () => {
        mainImg.removeAttribute('srcset');
        mainImg.removeAttribute('sizes');
        mainImg.src = newSrc;
        if (newAlt) mainImg.alt = newAlt;
      };
      preload.onerror = () => {};
      preload.src = newSrc;
    }

    thumbs.forEach((t) => {
      t.addEventListener('click', () => {
        const src = t.dataset.thumbSrc;
        if (src) swapMainImage(src, t.dataset.thumbAlt);
        thumbs.forEach((x) =>
          x.classList.toggle('mez-pdp__thumb--active', x === t)
        );
      });
    });

    // ---------- ATC submit guard ----------
    // <product-form> custom element owns the AJAX add + drawer-open flow.
    // We only intercept to block submission for Coming-Soon variants.
    if (form && atc) {
      form.addEventListener('submit', (e) => {
        if (atc.disabled) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }, { capture: true });
    }

    // ---------- Sticky ATC ----------
    if (stickyEl && atcWrap && stickyAtc) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            stickyEl.dataset.mezStickyVisible = entry.isIntersecting
              ? 'false'
              : 'true';
            stickyEl.hidden = false;
          });
        },
        { rootMargin: '0px 0px -90% 0px', threshold: 0 }
      );
      observer.observe(atcWrap);
      root.__mezObserver = observer;

      stickyAtc.addEventListener('click', () => {
        if (atc && !atc.disabled && !stickyAtc.disabled) {
          // Click the main ATC so <product-form>'s submit handler fires
          // (form.submit() bypasses event listeners; form.requestSubmit()
          // works but isn't supported everywhere).
          atc.click();
        }
      });
    }

    // Initial paint: ensure colors / state align with selected variant
    const initialVariant = resolveVariant();
    if (initialVariant) applyVariant(initialVariant);

    // Enforce initial mode (subscribe is default; sets selling_plan input + price)
    applyMode(state.mode);
  }

  function bootAll() {
    document.querySelectorAll('[data-mez-pdp]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  // Tear down before re-init when a section reloads in the theme editor
  document.addEventListener('shopify:section:unload', (e) => {
    if (e.target && e.target.querySelector) {
      const root = e.target.querySelector('[data-mez-pdp]');
      if (root && root.__mezObserver) {
        root.__mezObserver.disconnect();
        root.__mezObserver = null;
      }
    }
  });

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector) {
      const root = e.target.querySelector('[data-mez-pdp]');
      if (root) {
        // Clear init flag so listeners and observers re-bind on fresh DOM
        root.__mezInit = false;
        init(root);
      }
    }
  });
})();
